import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardContent } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";

const toDateInput = (value) => {
  if (!value) return "";
  // aceita ISO ("2025-12-15") ou datetime ("2025-12-15T00:00:00")
  const s = String(value);
  const iso = s.split("T")[0];
  // valida básico yyyy-mm-dd
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : "";
};

const EditCollaboratorModal = ({
  isOpen,
  onClose,
  collaborator,
  departments = [],
  managers = [],
  onSubmit,
  onDelete,
}) => {
  const initial = useMemo(() => {
    const c = collaborator || {};
    return {
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      email: c.email || "",
      birth_date: toDateInput(c.birth_date),
      hire_date: toDateInput(c.hire_date),
      job_title: c.job_title || "",
      department_id: c.department_id || "",
      department: c.department || "",
      cost_center: c.cost_center || "",
      manager_id: c.manager_id || "",
      country: c.country || "",
      is_active: typeof c.is_active === "boolean" ? c.is_active : true,
    };
  }, [collaborator]);

  const [form, setForm] = useState(initial);

  useEffect(() => {
    setForm(initial);
  }, [initial]);

  const currentDepartmentOption =
    !form.department_id && form.department
      ? { id: "__legacy__", name: form.department }
      : null;

  if (!isOpen) return null;

  const nameLabel = `${form.first_name} ${form.last_name}`.trim() || "Colaborador";

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      alert("Preencha Nome e Sobrenome.");
      return;
    }
    onSubmit?.(form);
  };

  const handleDelete = () => {
    if (!collaborator?.id) return;
    const ok = window.confirm(
      `Tem certeza que deseja deletar o colaborador "${nameLabel}"?`
    );
    if (!ok) return;
    onDelete?.(collaborator.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* modal */}
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Editar colaborador
                </h2>
                <p className="text-sm text-gray-500">{nameLabel}</p>
              </div>
              <Button variant="outline" onClick={onClose}>
                Fechar
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Nome</label>
                <Input
                  value={form.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder="Ex.: Paulo"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Sobrenome</label>
                <Input
                  value={form.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder="Ex.: Benfica"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-gray-600">E-mail (opcional)</label>
                <Input
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="ex.: nome@empresa.com"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Departamento</label>
                <select
                  value={form.department_id}
                  onChange={(e) => {
                    const selected = departments.find(
                      (department) => String(department.id) === e.target.value
                    );
                    setForm((prev) => ({
                      ...prev,
                      department_id: e.target.value,
                      department: selected?.name || "",
                      cost_center: selected?.cost_center || "",
                    }));
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione um departamento</option>
                  {currentDepartmentOption && (
                    <option value="__legacy__">{currentDepartmentOption.name}</option>
                  )}
                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Centro de custo</label>
                <Input
                  value={form.cost_center}
                  onChange={(e) => handleChange("cost_center", e.target.value)}
                  placeholder="Ex.: TI-ADM-001"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Cargo</label>
                <Input
                  value={form.job_title}
                  onChange={(e) => handleChange("job_title", e.target.value)}
                  placeholder="Ex.: Analista"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Gerente (responde a)</label>
                <select
                  value={form.manager_id}
                  onChange={(e) => handleChange("manager_id", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Sem gerente</option>
                  {managers
                    .filter((m) => String(m.id) !== String(collaborator?.id))
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {`${m.first_name || ""} ${m.last_name || ""}`.trim()}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">País</label>
                <Input
                  value={form.country}
                  onChange={(e) => handleChange("country", e.target.value)}
                  placeholder="Ex.: Brasil"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Status</label>
                <select
                  value={form.is_active ? "true" : "false"}
                  onChange={(e) =>
                    handleChange("is_active", e.target.value === "true")
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-600">Data de Nascimento</label>
                <Input
                  type="date"
                  value={form.birth_date}
                  onChange={(e) => handleChange("birth_date", e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Data de Admissão</label>
                <Input
                  type="date"
                  value={form.hire_date}
                  onChange={(e) => handleChange("hire_date", e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleDelete}
                disabled={!collaborator?.id}
              >
                Deletar
              </Button>

              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button onClick={handleSave}>Salvar</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditCollaboratorModal;
